import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../data/cloud-data.js';
import { Configuration, WheelTypes } from '../data/configuration.js';
import { WheelConfigurator } from '../configurator/wheel-configurator.js';
import { VehicleView } from './vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from './view-content.js';
import SelectedSectionItem from './ui/selected-section-item.tsx';
import { ModalContent } from './ui/modal.tsx'
import ViewContentRows from './ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface WheelsViewProps {
	wheelConfigurator: WheelConfigurator;
	onBackButtonClicked: () => void;
	onNextButtonClicked: () => void;
}

interface WheelViewProps {
	shouldStartClosed?: boolean;
	subHeading: string;
	wheelType: WheelTypes;
	itemId: string;
	itemName: string;
	sectionPrice: number;
	modalContent: ModalContent[];
	isClosed?: boolean;
	onPanelOpenClose?: (isOpen: boolean) => void;
	onItemClicked: (id: string) => void;
}

interface WheelItemsProps {
	wheelType: WheelTypes;
}

interface WheelsViewContextType {
	selectedFrontBrakeCaliperItemId?: string;
	handleFrontBrakeCaliperItemClicked?: (id: string) => void;
	selectedItemId?: string;
	handleItemClicked?: (id: string) => void;
}

// one flag per wheel type, as the paintwork view holds one per paint type. both models share the type and each simply leaves out
// the panels it does not offer, so the flags belonging to those panels are never read.
type WheelTypesAccordionState = {
	isSuperlitePanelClosed: boolean;
	isMultispokePanelClosed: boolean;
	isAerolitePanelClosed: boolean;
	isSportlitePanelClosed: boolean;
};

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function WheelsView(props: WheelsViewProps) {
	if (Configuration.isSupersport400Model()) {
		return WheelsView400(props);
	}
	else {
		return WheelsViewStandard(props);
	}
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

function WheelsView400(props: WheelsViewProps) {

	// initial values.
	const initialWheelItemId = Configuration.wheelStyle;
	const initialWheelItemName = () => getItemName(initialWheelItemId);
	const initialWheelType = CloudData.getWheelTypeForId(initialWheelItemId);
	const initialWheelTypeAccordionState = getAccordionStateForWheelType(initialWheelType);
	const initialFrontBrakeCaliperItemId = Configuration.frontBrakeCaliperOption;
	const initialFrontBrakeCaliperItemName = () => getFrontBrakeCaliperItemName(initialFrontBrakeCaliperItemId);

	// states.
	const [selectedWheelItemId, setSelectedWheelItemId] = useState(initialWheelItemId);
	const [selectedWheelItemName, setSelectedWheelItemName] = useState(initialWheelItemName);
	const [wheelTypeAccordionState, setWheelTypeAccordionState] = useState<WheelTypesAccordionState>(initialWheelTypeAccordionState);
	const [selectedFrontBrakeCaliperItemId, setSelectedFrontBrakeCaliperItemId] = useState(initialFrontBrakeCaliperItemId);
	const [selectedFrontBrakeCaliperItemName, setSelectedFrontBrakeCaliperItemName] = useState(initialFrontBrakeCaliperItemName);
	
	// handlers.

	const handleFrontBrakeCaliperItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedFrontBrakeCaliperItemId(id);
		setSelectedFrontBrakeCaliperItemName(getFrontBrakeCaliperItemName(id));

		// update the configuration.
		Configuration.frontBrakeCaliperOption = id;
		Configuration.updateCosts();

		// select the front brake caliper on the car.
		props.wheelConfigurator.selectFrontBrakeCaliper(id);
	};

	const handleWheelItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedWheelItemId(id);
		setSelectedWheelItemName(getItemName(id));

		// update the configuration.
		Configuration.wheelStyle = id;
		Configuration.updateCosts();

		// select the wheel style on the car.
		props.wheelConfigurator.selectWheelStyle(id);
	};

	const handlePanelOpenClose = (wheelTypePanel: WheelTypes, isOpen: boolean) => {

		// if opening a panel, open it and close the other panels.
		// if closing a panel, just close them all (as the others will be closed anyway).
		setWheelTypeAccordionState(getAccordionStateForWheelType(isOpen ? wheelTypePanel : null));
	};

	// return the HTML.
	return (
		<WheelsViewContext.Provider value={{ selectedFrontBrakeCaliperItemId, handleFrontBrakeCaliperItemClicked }}>
			<VehicleView
				onBackButtonClicked={props.onBackButtonClicked}
				onNextButtonClicked={props.onNextButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-wheels">
							WHEELS
						</h2>
					</div>

					<WheelView
						subHeading={'SPORTLITE'}
						wheelType={WheelTypes.Sportlite}
						itemId={selectedWheelItemId}
						itemName={selectedWheelItemName}
						onItemClicked={handleWheelItemClicked}
						sectionPrice={CloudData.getPrice(selectedWheelItemId, CloudData.wheelStyleOptions, { ref: false })}
						modalContent={CloudData.getModalContent(selectedWheelItemId, CloudData.wheelStyleOptions)}
						isClosed={wheelTypeAccordionState.isSportlitePanelClosed}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(WheelTypes.Sportlite, isOpen)}
					/>

					<WheelView
						subHeading={'AEROLITE'}
						wheelType={WheelTypes.Aerolite}
						itemId={selectedWheelItemId}
						itemName={selectedWheelItemName}
						onItemClicked={handleWheelItemClicked}
						sectionPrice={CloudData.getPrice(selectedWheelItemId, CloudData.wheelStyleOptions, { ref: false })}
						modalContent={CloudData.getModalContent(selectedWheelItemId, CloudData.wheelStyleOptions)}
						isClosed={wheelTypeAccordionState.isAerolitePanelClosed}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(WheelTypes.Aerolite, isOpen)}
					/>

					<SectionPanel
						subHeading={'FRONT BRAKE CALIPERS'}
						modalContent={CloudData.getModalContent(selectedFrontBrakeCaliperItemId, CloudData.accessoryOptions)}
					>
						<SelectedSectionItem
							itemName={selectedFrontBrakeCaliperItemName}
							sectionPrice={CloudData.getPrice(selectedFrontBrakeCaliperItemId, CloudData.accessoryOptions, { ref: false })}
						/>
						<FrontBrakeCaliperItems />
					</SectionPanel>
				</div>
			</VehicleView>
		</WheelsViewContext.Provider>
	);
}

function WheelsViewStandard(props: WheelsViewProps) {

	// initial values.
	const initialWheelItemId = Configuration.wheelStyle;
	const initialWheelItemName = () => getItemName(initialWheelItemId);
	const initialWheelType = CloudData.getWheelTypeForId(initialWheelItemId);
	const initialWheelTypeAccordionState = getAccordionStateForWheelType(initialWheelType);

	// states.
	const [selectedWheelItemId, setSelectedWheelItemId] = useState(initialWheelItemId);
	const [selectedWheelItemName, setSelectedWheelItemName] = useState(initialWheelItemName);
	const [wheelTypeAccordionState, setWheelTypeAccordionState] = useState<WheelTypesAccordionState>(initialWheelTypeAccordionState);

	// handlers.

	const handleWheelItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedWheelItemId(id);
		setSelectedWheelItemName(getItemName(id));

		// update the configuration.
		Configuration.wheelStyle = id;
		Configuration.updateCosts();

		// select the wheel style on the car.
		props.wheelConfigurator.selectWheelStyle(id);
	};

	const handlePanelOpenClose = (wheelTypePanel: WheelTypes, isOpen: boolean) => {

		// if opening a panel, open it and close the other panels.
		// if closing a panel, just close them all (as the others will be closed anyway).
		setWheelTypeAccordionState(getAccordionStateForWheelType(isOpen ? wheelTypePanel : null));
	};

	// return the HTML.
	return (
		<VehicleView
			onBackButtonClicked={props.onBackButtonClicked}
			onNextButtonClicked={props.onNextButtonClicked}>
			<div id="view-content">
				<div id="view-content-header">
					<h2 id="ga4-wheels">
						WHEELS
					</h2>
				</div>

				<WheelView
					subHeading={'SUPERLITE'}
					wheelType={WheelTypes.Superlite}
					itemId={selectedWheelItemId}
					itemName={selectedWheelItemName}
					onItemClicked={handleWheelItemClicked}
					sectionPrice={CloudData.getPrice(selectedWheelItemId, CloudData.wheelStyleOptions, { ref: false })}
					modalContent={CloudData.getModalContent(selectedWheelItemId, CloudData.wheelStyleOptions)}
					isClosed={wheelTypeAccordionState.isSuperlitePanelClosed}
					onPanelOpenClose={(isOpen) => handlePanelOpenClose(WheelTypes.Superlite, isOpen)}
				/>

				<WheelView
					subHeading={'MULTISPOKE'}
					wheelType={WheelTypes.Multispoke}
					itemId={selectedWheelItemId}
					itemName={selectedWheelItemName}
					onItemClicked={handleWheelItemClicked}
					sectionPrice={CloudData.getPrice(selectedWheelItemId, CloudData.wheelStyleOptions, { ref: false })}
					modalContent={CloudData.getModalContent(selectedWheelItemId, CloudData.wheelStyleOptions)}
					isClosed={wheelTypeAccordionState.isMultispokePanelClosed}
					onPanelOpenClose={(isOpen) => handlePanelOpenClose(WheelTypes.Multispoke, isOpen)}
				/>

				<WheelView
					subHeading={'AEROLITE'}
					wheelType={WheelTypes.Aerolite}
					itemId={selectedWheelItemId}
					itemName={selectedWheelItemName}
					onItemClicked={handleWheelItemClicked}
					sectionPrice={CloudData.getPrice(selectedWheelItemId, CloudData.wheelStyleOptions, { ref: false })}
					modalContent={CloudData.getModalContent(selectedWheelItemId, CloudData.wheelStyleOptions)}
					isClosed={wheelTypeAccordionState.isAerolitePanelClosed}
					onPanelOpenClose={(isOpen) => handlePanelOpenClose(WheelTypes.Aerolite, isOpen)}
				/>
			</div>
		</VehicleView>
	);
}

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const WheelsViewContext = createContext<WheelsViewContextType | undefined>(undefined);

function WheelView(props: WheelViewProps) {

	// set the selected item ID from the given prop.
	const selectedItemId = props.itemId;

	// if an item is clicked, call the given handler.
	const handleItemClicked = (id: string) => {
		props.onItemClicked(id);
	};

	// return the HTML.
	return (
		<WheelsViewContext.Provider value={{ selectedItemId, handleItemClicked }}>
			<SectionPanel
				shouldStartClosed={props.shouldStartClosed}
				subHeading={props.subHeading}
				modalContent={props.modalContent}
				onPanelOpenClose={props.onPanelOpenClose}
				isClosed={props.isClosed}
			>
				<SelectedSectionItem
					itemName={props.itemName}
					sectionPrice={props.sectionPrice}
				/>
				<WheelItems wheelType={props.wheelType} />
			</SectionPanel>
		</WheelsViewContext.Provider>
	);
}

function WheelItems(props: WheelItemsProps) {
	switch (props.wheelType) {
		case WheelTypes.Multispoke:
			return <WheelItemsMultispoke />;
		case WheelTypes.Aerolite:
			return <WheelItemsAerolite />;
		case WheelTypes.Sportlite:
			return <WheelItemsSportlite />;
		default:
			return <WheelItemsSuperlite />;
	}
};

function WheelItemsSuperlite() {
	return (
		<ViewContentRows>
			<WheelItem id={'W033'} imageFilePath={'superlite/silver'} />
			<WheelItem id={'W035'} imageFilePath={'superlite/frozen-grey'} />
			<WheelItem id={'W034'} imageFilePath={'superlite/black'} />
		</ViewContentRows>
	);
}

function WheelItemsMultispoke() {
	return (
		<ViewContentRows>
			<WheelItem id={'W003'} imageFilePath={'multispoke/silver'} />
			<WheelItem id={'W004'} imageFilePath={'multispoke/frozen-grey'} />
			<WheelItem id={'W026'} imageFilePath={'multispoke/black'} />
		</ViewContentRows>
	);
}

function WheelItemsAerolite() {
	return (
		<ViewContentRows>
			<WheelItem id={'W036'} imageFilePath={'aerolite/silver'} />
			<WheelItem id={'W037'} imageFilePath={'aerolite/anthracite'} />
		</ViewContentRows>
	);
}

function WheelItemsSportlite() {
	return (
		<ViewContentRows>
			<WheelItem id={'W038'} imageFilePath={'sportlite/silver'} />
			<WheelItem id={'W039'} imageFilePath={'sportlite/bronze'} />
		</ViewContentRows>
	);
}

function WheelItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(WheelsViewContext);
	if (!context) {
		throw new Error('WheelsViewContext must be used within a WheelsView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/wheels/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedItemId}
			onItemClicked={context.handleItemClicked!}
			shouldEnlarge={true}
		/>
	);
}

function FrontBrakeCaliperItems() {
	return (
		<ViewContentRows>
			<FrontBrakeCaliperItem id={'BCF001'} imageFilePath={'black'} />
			<FrontBrakeCaliperItem id={'BCF002'} imageFilePath={'coral'} />
		</ViewContentRows>
	);
}

function FrontBrakeCaliperItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(WheelsViewContext);
	if (!context) {
		throw new Error('WheelsViewContext must be used within a WheelsView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getFrontBrakeCaliperItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/wheels/front-brake-caliper/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedFrontBrakeCaliperItemId}
			onItemClicked={context.handleFrontBrakeCaliperItemClicked!}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// returns the accordion state that has the given wheel type's panel open and every other panel closed, or - given null - has them
// all closed. this is both how the accordion starts, opened on the panel holding the current selection, and what every opening or
// closing of a panel produces.
function getAccordionStateForWheelType(openWheelType: WheelTypes | null): WheelTypesAccordionState {
	return {
		isSuperlitePanelClosed: openWheelType !== WheelTypes.Superlite,
		isMultispokePanelClosed: openWheelType !== WheelTypes.Multispoke,
		isAerolitePanelClosed: openWheelType !== WheelTypes.Aerolite,
		isSportlitePanelClosed: openWheelType !== WheelTypes.Sportlite
	};
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.wheelStyleOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getFrontBrakeCaliperItemName(id: string): string {
	return CloudData.getName(id, CloudData.accessoryOptions);
}